// The welcome screen's pure half — 05i-2. main.js's welcome-card grid cells, the ambient
// ghosts' "is this spot free?", their random walk, where a merge's replacement lands and
// how a merged ghost grows — copied exactly, with the random numbers passed in so they can
// be tested. The live half (welcome.ts) owns the elements and the timers.

export interface Cell {
  col: number;
  row: number;
  cols: number;
  rows: number;
}

export interface Ghost extends Cell {
  id: number;
  /** Sitting out the random walk while it merges. */
  merging?: boolean;
}

export type Random = () => number;

// One size vocabulary for the first 12 ghosts and every replacement, so the two never drift.
export const GHOST_SIZES: readonly { cols: number; rows: number }[] = [
  { cols: 1, rows: 2 }, { cols: 2, rows: 1 }, { cols: 2, rows: 2 },
  { cols: 1, rows: 3 }, { cols: 3, rows: 1 }, { cols: 2, rows: 3 },
  { cols: 3, rows: 2 }, { cols: 3, rows: 3 },
];

export const INITIAL_GHOSTS = 12;
/** Ids 0–11 are the first ghosts; replacements count up from here, so an id a merge
 *  retired is never reused (the free-spot check skips a ghost by id). */
export const FIRST_REPLACEMENT_ID = 12;
// A fixed cadence (Ben: "one merge every 3s", not a dice roll per tick).
export const GHOST_MERGE_INTERVAL_MS = 3000;
export const GHOST_FLEX_INTERVAL_MS = 800;
/** Before any welcome card is measured — a rect nothing can overlap. */
export const NO_WELCOME: Cell = { col: -99, row: -99, cols: 0, rows: 0 };

/** The grid cells the welcome card covers, with 60px of clearance around it (capped at 24,
 *  as main.js did). Ghosts keep off it, and so does a phone tile tap's first empty slot. */
export function welcomeRectFor(centerX: number, centerY: number, textW: number, textH: number, step: number): Cell {
  const PAD = 60;
  const startCol = Math.max(0, Math.floor((centerX - textW / 2 - PAD) / step));
  const endCol = Math.min(24, Math.ceil((centerX + textW / 2 + PAD) / step));
  const startRow = Math.max(0, Math.floor((centerY - textH / 2 - PAD) / step));
  const endRow = Math.min(24, Math.ceil((centerY + textH / 2 + PAD) / step));
  return { col: startCol, row: startRow, cols: endCol - startCol, rows: endRow - startRow };
}

/** How far ghosts may roam — recomputed each time, so a respawn long after load still fits
 *  the current window. */
export function visibleGhostBounds(innerWidth: number, innerHeight: number, step: number): { maxVisCol: number; maxVisRow: number } {
  return {
    maxVisCol: Math.max(5, Math.floor((innerWidth - 56) / step)),
    maxVisRow: Math.max(5, Math.floor((innerHeight - 16) / step)),
  };
}

const overlaps = (a: Cell, b: Cell): boolean =>
  a.col < b.col + b.cols && a.col + a.cols > b.col && a.row < b.row + b.rows && a.row + a.rows > b.row;

export interface GhostWorld {
  gridCols: number;
  gridRows: number;
  welcome: Cell;
  ghosts: readonly Ghost[];
}

/** main.js isAutoAreaClear: on the canvas, within 5 cells of the welcome card (so the ghosts
 *  frame it), not on it, and not on another ghost (except `ignoreId`). */
export function isAutoAreaClear(t: Cell, ignoreId: number, w: GhostWorld): boolean {
  if (t.col < 0 || t.row < 0) return false;
  if (t.col + t.cols > w.gridCols || t.row + t.rows > w.gridRows) return false;
  const text = w.welcome;
  if (
    t.col < text.col - 5 ||
    t.col + t.cols > text.col + text.cols + 5 ||
    t.row < text.row - 5 ||
    t.row + t.rows > text.row + text.rows + 5
  ) {
    return false;
  }
  if (overlaps(t, text)) return false;
  return !w.ghosts.some((g) => g.id !== ignoreId && overlaps(t, g));
}

const randomSize = (random: Random) => GHOST_SIZES[Math.floor(random() * GHOST_SIZES.length)]!;

/**
 * One of the first ghosts (main.js's spawn loop): up to 50 random draws. Kept exactly,
 * including its quirk — the 50th spot is drawn but never checked (`attempts < 50 && …`
 * stops first), so at most 49 are tried.
 */
export function initialGhostCell(
  random: Random,
  bounds: { maxVisCol: number; maxVisRow: number },
  isClear: (c: Cell) => boolean,
): Cell | null {
  let cell: Cell;
  let attempts = 0;
  do {
    const sz = randomSize(random);
    cell = {
      cols: sz.cols,
      rows: sz.rows,
      col: Math.max(0, Math.floor(random() * (bounds.maxVisCol - sz.cols))),
      row: Math.max(0, Math.floor(random() * (bounds.maxVisRow - sz.rows))),
    };
    attempts++;
  } while (attempts < 50 && !isClear(cell));
  return attempts < 50 ? cell : null;
}

/** main.js flexBlock: one step of the random walk — nudge position and size by -1 / 0 / +1
 *  each (size kept 2–4), first of 10 tries that fits. Null = stay put (and while merging). */
export function flexStep(g: Ghost, random: Random, isClear: (c: Cell) => boolean): Cell | null {
  if (g.merging) return null;
  for (let i = 0; i < 10; i++) {
    const dCol = Math.floor(random() * 3) - 1;
    const dRow = Math.floor(random() * 3) - 1;
    const dCols = Math.floor(random() * 3) - 1;
    const dRows = Math.floor(random() * 3) - 1;
    const next = {
      col: g.col + dCol,
      row: g.row + dRow,
      cols: Math.min(4, Math.max(2, g.cols + dCols)),
      rows: Math.min(4, Math.max(2, g.rows + dRows)),
    };
    if (isClear(next)) return next;
  }
  return null;
}

/** main.js spawnGhost's spot: near the target (jittered ±3, so it doesn't look snapped),
 *  else anywhere clear on screen — so "one merge, one new ghost" rarely fails. */
export function spawnCell(
  targetCol: number,
  targetRow: number,
  random: Random,
  bounds: { maxVisCol: number; maxVisRow: number },
  isClear: (c: Cell) => boolean,
): Cell | null {
  const JITTER = 3;
  for (let attempts = 0; attempts < 40; attempts++) {
    const sz = randomSize(random);
    const jCol = targetCol + Math.floor(random() * (JITTER * 2 + 1)) - JITTER;
    const jRow = targetRow + Math.floor(random() * (JITTER * 2 + 1)) - JITTER;
    const cell = {
      cols: sz.cols,
      rows: sz.rows,
      col: Math.max(0, Math.min(Math.max(0, bounds.maxVisCol - sz.cols), jCol)),
      row: Math.max(0, Math.min(Math.max(0, bounds.maxVisRow - sz.rows), jRow)),
    };
    if (isClear(cell)) return cell;
  }
  for (let attempts = 0; attempts < 50; attempts++) {
    const sz = randomSize(random);
    const cell = {
      cols: sz.cols,
      rows: sz.rows,
      col: Math.max(0, Math.floor(random() * Math.max(1, bounds.maxVisCol - sz.cols))),
      row: Math.max(0, Math.floor(random() * Math.max(1, bounds.maxVisRow - sz.rows))),
    };
    if (isClear(cell)) return cell;
  }
  return null;
}

/** main.js attemptGhostMerge's pick: two different ghosts not already merging. */
export function pickMergePair<T extends Ghost>(ghosts: readonly T[], random: Random): [T, T] | null {
  const candidates = ghosts.filter((g) => !g.merging);
  if (candidates.length < 2) return null;
  const a = candidates[Math.floor(random() * candidates.length)]!;
  const others = candidates.filter((g) => g !== a);
  const b = others[Math.floor(random() * others.length)]!;
  return [a, b];
}

/** The merged ghost's new size: one bigger than the larger of the two, capped at 4. */
export function grownSize(a: Cell, b: Cell): { cols: number; rows: number } {
  return { cols: Math.min(4, Math.max(a.cols, b.cols) + 1), rows: Math.min(4, Math.max(a.rows, b.rows) + 1) };
}

/** Where a merge's replacement is aimed: mirrored to the other side of the screen, so the
 *  ghost count never drains away as merges happen. */
export function mirroredTarget(b: Cell, bounds: { maxVisCol: number; maxVisRow: number }): { col: number; row: number } {
  return { col: Math.max(0, bounds.maxVisCol - b.col - b.cols), row: Math.max(0, bounds.maxVisRow - b.row - b.rows) };
}
